/**
 * Cliente del backend.
 *
 * Cada llamada pide un JWT de sesión fresco al admin y lo manda en `Authorization`. El
 * backend verifica la firma con el client_secret y de ahí saca el `storeId`.
 *
 * *** Este token no sirve para llamar a la API de Tienda Nube. *** Es sólo prueba de
 * identidad frontend → backend.
 */

import { getSessionToken } from '@tiendanube/nexo';
import { nexo } from './nexo';
import {
  crearPedidorDeToken,
  fetchConVencimiento,
  VencimientoError,
} from './transporte';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /**
   * Identificador corto de la ocurrencia, el mismo que quedó en el log del Worker.
   * `null` si la respuesta no lo trajo (un error de red, o una respuesta sin cuerpo).
   */
  readonly id: string | null;

  constructor(status: number, code: string, message: string, id: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.id = id;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; id?: string };
}

/**
 * Cuánto se espera al admin por un token de sesión.
 *
 * Es un `postMessage` al frame padre: en condiciones normales tarda milisegundos. Ocho
 * segundos no es un presupuesto, es el punto a partir del cual dar por hecho que no va a
 * contestar nunca — que es lo que puede pasar, porque la promesa de Nexo no tiene
 * vencimiento propio. Ver `transporte.ts`.
 */
const ESPERA_MAX_DE_TOKEN_MS = 8_000;

/**
 * Tope de una llamada al backend.
 *
 * Generoso a propósito: el preview de un catálogo grande tarda de verdad. Lo que se corta
 * no es la lentitud sino el cuelgue.
 */
const ESPERA_MAX_DE_LLAMADA_MS = 90_000;

const pedirToken = crearPedidorDeToken(() => getSessionToken(nexo), ESPERA_MAX_DE_TOKEN_MS);

/** Un vencimiento se cuenta como error de comunicación, con un texto que dice qué hacer. */
function comoApiError(error: unknown): unknown {
  if (error instanceof VencimientoError) {
    return new ApiError(0, 'sin_respuesta', `${error.queEs} no respondió. Probá de nuevo.`);
  }
  return error;
}

async function pedir(path: string, init: RequestInit = {}): Promise<Response> {
  let response: Response;
  try {
    const token = await pedirToken();
    response = await fetchConVencimiento(
      fetch,
      `/api${path}`,
      {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
      ESPERA_MAX_DE_LLAMADA_MS,
    );
  } catch (error) {
    throw comoApiError(error);
  }

  if (!response.ok) {
    let body: ErrorBody = {};
    try {
      body = (await response.json()) as ErrorBody;
    } catch {
      // Sin cuerpo JSON: se usa el mensaje genérico de abajo.
    }
    throw new ApiError(
      response.status,
      body.error?.code ?? 'desconocido',
      body.error?.message ?? 'No pudimos comunicarnos con el servidor. Probá de nuevo en un rato.',
      body.error?.id ?? null,
    );
  }
  return response;
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await (await pedir(path, init)).json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return json<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

// --- Tipos compartidos con el backend ---------------------------------------

export type CampoAfectado = 'price' | 'promotional_price' | 'both';
export type Direccion = 'up' | 'down' | 'nearest';
export type EstadoItem = 'pending' | 'succeeded' | 'failed' | 'skipped';

export interface Alcance {
  allProducts: boolean;
  categoryIds: number[];
  includeSubcategories: boolean;
  brands: string[];
  priceMin: number | null;
  priceMax: number | null;
  onlyPublished: boolean;
  onlyVisible: boolean;
  exclude: {
    categoryIds: number[];
    brands: string[];
    productIds: number[];
    variantIds: number[];
  };
}

export type Ajuste =
  | { kind: 'percentage'; percent: number }
  | { kind: 'fixed_amount'; amount: number }
  | { kind: 'set_price'; amount: number }
  | { kind: 'target_margin'; percent: number; basis: 'markup' | 'margin' };

export type Redondeo =
  | { kind: 'none' }
  | { kind: 'ending'; ending: '900' | '990' | '99' | '00'; direction: Direccion }
  | { kind: 'multiple'; multiple: 100 | 500 | 1000; direction: Direccion };

export interface Regla {
  scope: Alcance;
  adjustment: Ajuste;
  rounding: Redondeo;
  targetFields: CampoAfectado;
}

/**
 * Plan comercial, tal como lo devuelve el backend.
 *
 * Ninguno de estos números se escribe en el frontend: salen de configuración y se pueden
 * mover sin desplegar la UI.
 */
export interface Plan {
  codigo: string;
  moneda: string;
  precioMensual: number;
  precioTexto: string;
  diasDePrueba: number;
  diasDeRetencionSnapshot: number;
}

export interface Sesion {
  tienda: {
    id: string;
    nombre: string | null;
    moneda: string | null;
    pais: string | null;
    plan: string | null;
    estado: string;
  };
  suscripcion: {
    plan: string;
    estado: string;
    pruebaHasta: string | null;
    /** Días que faltan para que se termine la prueba. `null` si no está en prueba. */
    diasRestantes: number | null;
    /**
     * Si la fecha de fin de prueba ya pasó. **No bloquea nada**: quién cobra y cuándo lo
     * decide Tienda Nube. Está sólo para no decir "hoy se termina tu prueba" para siempre,
     * porque `diasRestantes` da 0 el último día y también tres semanas después.
     */
    pruebaTermino: boolean;
  } | null;
  /**
   * Si la tienda puede empezar una actualización.
   *
   * *** El que decide es el backend. *** Esto sólo evita ofrecer un botón que va a
   * devolver 402; la compuerta de verdad está en `exigirSuscripcion` del Worker.
   */
  acceso: {
    puedeActualizar: boolean;
    motivo: string | null;
    mensaje: string | null;
  };
  plan: Plan;
}

export interface Categoria {
  id: number;
  nombre: string;
  ruta: string;
  padre: number | null;
}

export interface Marca {
  clave: string;
  nombre: string;
  productos: number;
  grafias: string[];
}

export interface ConteoDeAlcance {
  productos: number | null;
  variantes: number | null;
}

export interface ResumenPreview {
  corridaId: string;
  reglaId: string | null;
  totales: {
    analizadas: number;
    aActualizar: number;
    productos: number;
    sinCambios: number;
    salteadas: number;
    conAvisos: number;
  };
  avisos: Array<{ codigo: string; cantidad: number; mensaje: string }>;
  /**
   * Uno o dos párrafos. El segundo sólo aparece cuando la regla toca únicamente el precio de
   * lista: ahí las integraciones que sincronizan por fecha pueden no detectar el cambio.
   */
  avisoIntegraciones: string[] | null;
  /**
   * Cuánto va a tardar, estimado sobre los lotes reales. `texto` ya viene armado y con la
   * razón incluida cuando son las ofertas las que la hacen lenta: no se rearma acá para que
   * el motor y la pantalla no puedan decir cosas distintas.
   */
  duracion: { ms: number; texto: string; porOfertas: boolean; requests: number };
}

export interface Corrida {
  id: string;
  origen: 'manual' | 'scheduled' | 'undo';
  estado:
    | 'preview'
    | 'queued'
    | 'running'
    | 'completed'
    | 'completed_with_errors'
    | 'failed'
    | 'cancelled'
    | 'undone';
  reglaId: string | null;
  /**
   * La regla que se aplicó, para poder distinguir una corrida de otra en el historial.
   *
   * `null` cuando no hay ninguna: las corridas de deshacer no tienen regla propia. Y
   * `spec: null` cuando la regla existe pero el backend no la pudo leer con el esquema de
   * hoy — queda el nombre, que ya dice algo.
   */
  regla: { nombre: string; spec: Regla | null } | null;
  deshaceA: string | null;
  totales: { analizadas: number; actualizadas: number; fallidas: number; salteadas: number };
  creadaEn: string;
  iniciadaEn: string | null;
  terminadaEn: string | null;
  sePuedeDeshacerHasta: string | null;
  error: string | null;
  duracionEstimadaMs: number | null;
  /** Sólo mientras corre, y sólo si se puede decir algo honesto. `null` si no. */
  restante: { ms: number; texto: string } | null;
}

export interface ItemCorrida {
  variante: number;
  producto: string;
  etiqueta: string;
  sku: string | null;
  precioActual: number | null;
  precioNuevo: number | null;
  ofertaActual: number | null;
  ofertaNueva: number | null;
  estado: EstadoItem;
  motivo: string | null;
  avisos: string[];
  error: string | null;
}

export interface PaginaItems {
  items: ItemCorrida[];
  total: number;
  pagina: number;
  porPagina: number;
}

/**
 * Recurrencia.
 *
 * *** Un `once` se expresa en hora de Buenos Aires, no en UTC. *** La conversión la hace
 * el backend, que es donde está probada: el navegador del comerciante puede estar en
 * cualquier zona y "las 9" tiene que ser las 9 en Buenos Aires siempre. Por eso
 * `fechaLocal` y `horaLocal` son lo que se manda, y `at` sólo viene de vuelta.
 */
export type Recurrencia =
  | { kind: 'once'; at?: string; fechaLocal: string; horaLocal: string }
  | { kind: 'monthly'; day: number; hour: number; minute: number }
  | { kind: 'weekly'; weekday: number; hour: number; minute: number }
  | { kind: 'every_n_days'; days: number; hour: number; minute: number };

export interface Programacion {
  id: string;
  reglaId: string;
  reglaNombre: string;
  descripcion: string;
  proximaEjecucion: string;
  proximaEjecucionTexto: string;
  ultimaEjecucion: string | null;
  activa: boolean;
}

/** Lo que necesita el formulario de edición para venir precargado. */
export interface ProgramacionDetalle {
  id: string;
  reglaId: string;
  /** `null` si la regla fue borrada. La programación sigue existiendo. */
  nombre: string | null;
  regla: Regla | null;
  recurrencia: Recurrencia;
  descripcion: string;
  proximaEjecucion: string;
  proximaEjecucionTexto: string;
  ultimaEjecucion: string | null;
  activa: boolean;
}

// --- Endpoints ---------------------------------------------------------------

/**
 * El árbol de categorías, pedido una sola vez por sesión.
 *
 * *** No es una optimización, es cuidado del rate limit. *** `/catalogo/categorias` le
 * pega a la API de Tienda Nube, y ese límite es **compartido con todas las apps** que el
 * comerciante tenga instaladas: si lo consumimos nosotros, le rompemos la sincronización
 * con Mercado Libre o con su ERP. Ahora lo piden dos pantallas —el wizard, para elegir, y
 * el historial, para poder escribir "en Relojería" en vez de "en 1 categoría"— así que
 * conviene que la segunda no vuelva a salir a la red.
 *
 * Se guarda la promesa y no el resultado: dos pantallas que lo piden a la vez comparten
 * la misma llamada. Y si falla, se borra, para que el próximo intento vuelva a probar en
 * vez de quedar pegado a un error.
 */
let categoriasEnVuelo: Promise<{ categorias: Categoria[] }> | null = null;

export const api = {
  sesion: (): Promise<Sesion> => json<Sesion>('/session'),

  categorias: (): Promise<{ categorias: Categoria[] }> => {
    categoriasEnVuelo ??= json<{ categorias: Categoria[] }>('/catalogo/categorias').catch(
      (error: unknown) => {
        categoriasEnVuelo = null;
        throw error;
      },
    );
    return categoriasEnVuelo;
  },

  marcas: (): Promise<{ marcas: Marca[] }> => json<{ marcas: Marca[] }>('/catalogo/marcas'),

  /**
   * Cuánto agarra un alcance, sin calcular ningún precio.
   *
   * `null` en los dos campos significa "no lo pudimos contar": el catálogo se pasó del
   * presupuesto de barrido. No es un error y no hay nada que mostrar.
   */
  alcance: (alcance: Alcance): Promise<ConteoDeAlcance> =>
    post<ConteoDeAlcance>('/catalogo/alcance', { alcance }),

  preview: (nombre: string, regla: Regla, guardarRegla: boolean): Promise<ResumenPreview> =>
    post<ResumenPreview>('/corridas/preview', { nombre, regla, guardarRegla }),

  corridas: (): Promise<{ corridas: Corrida[] }> => json<{ corridas: Corrida[] }>('/corridas'),

  corrida: (id: string): Promise<{ corrida: Corrida }> =>
    json<{ corrida: Corrida }>(`/corridas/${id}`),

  items: (id: string, opciones: { pagina?: number; q?: string; estado?: EstadoItem } = {}): Promise<PaginaItems> => {
    const query = new URLSearchParams();
    if (opciones.pagina !== undefined) query.set('pagina', String(opciones.pagina));
    if (opciones.q !== undefined && opciones.q !== '') query.set('q', opciones.q);
    if (opciones.estado !== undefined) query.set('estado', opciones.estado);
    return json<PaginaItems>(`/corridas/${id}/items?${query.toString()}`);
  },

  urlCsv: (id: string): string => `/api/corridas/${id}/csv`,

  /**
   * El CSV se baja con el token en el header, así que no alcanza con un `<a href>`:
   * se pide con fetch y se dispara la descarga desde un blob.
   */
  descargarCsv: async (id: string): Promise<void> => {
    const response = await pedir(`/corridas/${id}/csv`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `precios-al-dia-${id.slice(0, 8)}.csv`;
    document.body.appendChild(enlace);
    enlace.click();
    enlace.remove();
    URL.revokeObjectURL(url);
  },

  ejecutar: (id: string): Promise<{ estado: string; lotes: number; variantes: number }> =>
    post(`/corridas/${id}/ejecutar`),

  cancelar: (
    id: string,
  ): Promise<{
    estado: string;
    /** Cuántos precios quedaron cambiados en la tienda antes del freno. */
    actualizadas: number;
    salteadas: number;
    sinProcesar: number;
    sePuedeDeshacer: boolean;
  }> => post(`/corridas/${id}/cancelar`),

  deshacer: (
    id: string,
  ): Promise<{
    corridaId: string;
    aRestaurar: number;
    modificadasDespues: number;
    yaEstaban: number;
    borradas: number;
    aviso: string | null;
  }> => post(`/corridas/${id}/deshacer`),

  programaciones: (): Promise<{ programaciones: Programacion[] }> =>
    json<{ programaciones: Programacion[] }>('/programaciones'),

  crearProgramacion: (body: {
    nombre: string;
    recurrencia: Recurrencia;
    reglaId?: string;
    regla?: Regla;
  }): Promise<{ id: string; descripcion: string; proximaEjecucionTexto: string }> =>
    post('/programaciones', body),

  programacion: (id: string): Promise<{ programacion: ProgramacionDetalle }> =>
    json<{ programacion: ProgramacionDetalle }>(`/programaciones/${id}`),

  /** Sólo se manda lo que cambió: lo que no viaja, queda como está. */
  editarProgramacion: (
    id: string,
    body: { nombre?: string; recurrencia?: Recurrencia; regla?: Regla },
  ): Promise<{ id: string; descripcion: string; proximaEjecucionTexto: string }> =>
    json(`/programaciones/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  pausarProgramacion: (id: string): Promise<{ activa: boolean }> =>
    post(`/programaciones/${id}/pausar`),

  activarProgramacion: (id: string): Promise<{ activa: boolean }> =>
    post(`/programaciones/${id}/activar`),

  borrarProgramacion: (id: string): Promise<void> =>
    pedir(`/programaciones/${id}`, { method: 'DELETE' }).then(() => undefined),
};
